// Election Day Pulse — polling unit critical event types.
// Mirrors the backend PollingUnitEventType enum (int 1..8); the backend
// persists enum NAMES in Mongo but the record-event request takes the int.

export const PU_EVENT_TYPE = {
	OfficialArrived: 1,
	MaterialsArrived: 2,
	AccreditationCommenced: 3,
	VotingCommenced: 4,
	VotingStopped: 5,
	CountingCommenced: 6,
	CountingStopped: 7,
	ResultsAnnounced: 8,
} as const;

export type PuEventType = (typeof PU_EVENT_TYPE)[keyof typeof PU_EVENT_TYPE];

export const PU_EVENT_ORDER: PuEventType[] = [1, 2, 3, 4, 5, 6, 7, 8];

export const PU_EVENT_LABELS: Record<PuEventType, string> = {
	1: 'Official Arrived',
	2: 'Materials Arrived',
	3: 'Accreditation Commenced',
	4: 'Voting Commenced',
	5: 'Voting Stopped',
	6: 'Counting Commenced',
	7: 'Counting Stopped',
	8: 'Results Announced',
};

/** Short imperative hint shown under the capture CTA. */
export const PU_EVENT_HINTS: Record<PuEventType, string> = {
	1: 'Tap when the INEC official arrives at your polling unit',
	2: 'Tap when electoral materials arrive at your polling unit',
	3: 'Tap when voter accreditation begins',
	4: 'Tap when the first vote is cast',
	5: 'Tap when voting closes at your polling unit',
	6: 'Tap when vote counting begins',
	7: 'Tap when vote counting is concluded',
	8: 'Tap when the results are announced at your polling unit',
};

export interface IPuEventLocation {
	address: string;
	latitude: string; // strings per backend contract (Domain Location model)
	longitude: string;
	plusCode?: string | null;
}

/** POST body for {COMMANDAPI}/api/v1/i-elect/PollingUnitEvent/record-event. */
export interface IRecordPuEventRequest {
	referenceId: string; // client uuid — idempotency key, stable across retries
	poolingUnitCode: string;
	election: { id: number; name: string };
	eventType: PuEventType;
	occurredAt: string; // ISO 8601, client clock — authoritative by design
	location: IPuEventLocation;
	accuracy: number;
	mediaUrls: string[];
	resultSource: 2; // always Agent in this app
	notes: string;
}

/** BaseResponse envelope from the command write. */
export interface IRecordPuEventResponse {
	message: string;
	isSuccessful: boolean;
	statusCode: number;
	data?: {
		id: string;
		status: 'Created' | 'Duplicate' | 'Failed';
		referenceId: string;
		mediaUrls?: string[];
	};
}

/** One slot from {QUERYAPI}/api/v1/i-elect/PollingUnitEvent/get-checklist (bare body). */
export interface IPuEventChecklistSlot {
	eventType: PuEventType;
	eventTypeName: string;
	captured: boolean;
	occurredAt?: string | null;
	location?: IPuEventLocation | null;
	accuracy?: number | null;
	mediaUrls?: string[];
	referenceId?: string | null;
	notes?: string | null;
}

export interface IPuEventChecklistResponse {
	electionId: number;
	poolingUnitCode: string;
	resultSource: string;
	slots: IPuEventChecklistSlot[];
}

/** Outcome union shared by the standalone client (mirrors vesr/agent-assignment). */
export type PuEventsOutcome<T> =
	| { reachable: false }
	| {
			reachable: true;
			httpStatus: number;
			body?: T;
			errorMessage?: string;
			/** Coded refusal, e.g. `UNIT_CANCELLED` (409) once INEC cancelled the unit. */
			errorCode?: string;
	  };

/** The server refused a capture because the unit is cancelled — terminal, never retried. */
export const UNIT_CANCELLED = 'UNIT_CANCELLED';

// ── Local queue (persisted to localStorage until confirmed by the server) ──

/**
 * 'armed' = persisted during the undo grace window (crash-recoverable but NOT
 * yet counted as captured); promoted to 'pending' on commit or after a reload.
 */
export type PuEventQueueStatus = 'armed' | 'pending' | 'sending' | 'failed';

export interface IPuEventQueueItem {
	referenceId: string;
	request: IRecordPuEventRequest;
	capturedAt: string; // client ISO timestamp, drives the optimistic UI
	status: PuEventQueueStatus;
	retryCount: number;
	/** Set when GPS could not be resolved at capture time. */
	gpsDegraded?: boolean;
}

// ── Capture state machine ──────────────────────────────────────────────────

export type CapturePhase = 'idle' | 'armed' | 'committing' | 'sent' | 'queued-offline' | 'failed';

export interface ICaptureState {
	phase: CapturePhase;
	eventType: PuEventType | null;
	referenceId: string | null;
	undoSecondsLeft: number;
	lastError: string | null;
	/** The server refused for good (e.g. the unit is cancelled) — retrying cannot succeed. */
	lastErrorFinal?: boolean;
}

// ── Merged checklist view (server slots + local queue reconciliation) ─────

export type PuSlotStatus = 'captured' | 'syncing' | 'failed' | 'pending';

export interface IMergedSlot {
	eventType: PuEventType;
	label: string;
	status: PuSlotStatus;
	occurredAt: string | null;
	location: IPuEventLocation | null;
	accuracy: number | null;
	mediaUrls: string[];
	referenceId: string | null;
	gpsDegraded: boolean;
	/** True when the captured record is server-confirmed (not just local). */
	confirmed: boolean;
}

// ── Panel data contract (rsProps.callSection data) ─────────────────────────

export interface IElectionEventsPanelData {
	puCode: string;
	puName: string;
	electionId: number;
	electionName: string;
}
